// Sugalarm — thin top-of-screen banner for `low` episodes (cycle 33).
//
// Cycle 32 and earlier treated every alarming level (low / urgent_low /
// urgent_high) the same way — full-screen red takeover via NowScreen's
// alarm branch or the synthetic UrgentOverlay. Cycle 33 reserves the
// full takeover for urgent levels and demotes regular `low` to this
// thin banner: alarm audio still plays (the cycle-15 episode bridge
// in `useAlarmSounds` is unchanged) and the same Address / Silence
// state machine drives the button, but the rest of the Now screen
// stays usable so the user can still see the hero number, glance at
// the trend, and pre-treat without losing context.
//
// Phase logic (cycle 62 — Snooze + Stop two-button model):
//   active && !hasSnoozed && !stopped → "Snooze" + "Stop" buttons
//   phase === "snoozed"               → "Snoozing M:SS" countdown text
//   stopped                           → muted "Stopped" tag + Snooze
//
// "Stop" mutes the current episode locally; the recovery + re-arm
// rule in useAlarmEpisode keeps watching the reading stream and a
// worsening crossing (low → urgent_low) re-fires audio + a fresh
// banner state. See `useAlarmEpisode.ts` header for the full model.
//
// Visual (cycle 452.2): 52 px tall with 44 px buttons, opaque
// `--alarm-warn-fill` ground with `--alarm-warn-ink` foreground (see
// alarmBannerStyle.ts — shared with the off-Now banner),
// pulsing dot on the left, sgv on the left, action on the right. No
// full-page flash — the cycle-prompt explicitly calls for a subtle
// presentation. Rendered above NowScreen's grid so it sits flush
// under KioskHeader / MobileFrame header. `aria-live="polite"` so
// screen readers announce the level transition without interrupting.

import { displayValue } from "../../lib/glucose";
import type { AlarmEpisodeState } from "../../hooks/useAlarmEpisode";
import type { FrameVariant } from "../../hooks/useFrameVariant";
// Cycle 166: presentation-only read of the device-local clock-mode
// store — gates the expanded (screen-covering) form below. No alarm
// logic consults clock mode; this is purely WHICH chrome the same
// episode state renders.
import { useClockMode } from "../../hooks/useAlarmClockMode";
import {
  ALARM_BANNER_PAD_Y,
  ALARM_BANNER_ROW_PX,
  alarmBannerDot,
  alarmBannerGround,
} from "./alarmBannerStyle";
// Cycle 460: the shared alarm pill (filled / outlined) — the high banner's
// Stop-pill contract, never a fork of it.
import {
  AlarmPill,
  AlarmPillPair,
  LOW_SNOOZE_LABEL,
  LOW_SNOOZE_SHORT_LABEL,
  LOW_STOP_LABEL,
  LOW_STOP_SHORT_LABEL,
} from "./AlarmStopPill";

interface Props {
  episode: AlarmEpisodeState;
  units: "mg/dL" | "mmol/L";
  // Cycle 303: on the mobile frame the compact strip DROPS the glucose
  // reading (visible on the Now hero anyway) and merges the status into
  // one ellipsizing text block so a long status can never squish the
  // Snooze/Stop cluster on a 375 px viewport. Kiosk/web are byte-
  // identical (the reading + right-anchored status stay). Defaults to a
  // non-mobile presentation when the prop is absent.
  variant?: FrameVariant;
}

function fmtSnooze(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${mm}:${String(ss).padStart(2, "0")}`;
}

export function LowAlarmBanner({ episode, units, variant }: Props) {
  // Cycle 303: mobile-compact presentation (drop reading, ellipsize).
  const mobile = variant === "mobile";
  // Hook before the early returns (Rules of Hooks).
  const clockModeActive = useClockMode().active;
  // ⚠ CYCLE 461 (operator decision, 2026-10-09): THE LOW BANNER IS THE HIGH
  // BANNER'S HEIGHT ON A PHONE. Cycle 452.2's portrait stacking (label line,
  // then the controls on their own line — 74.25 px) is gone from this banner:
  // it is one row, laid out exactly as `HighAlarmBanner`'s (the label is the
  // flexible, wrapping text block; the pills never shrink). To fit that row
  // on a 390 / 375 / 360 phone the pills read the short words ("Snooze" /
  // "Stop" — `AlarmPill`'s `shortLabel`, which CSS shows at the portrait-
  // phone breakpoint; cycle 460 did this for the bedside strip only). A
  // landscape phone, the kiosk and the web read "Snooze sounds" /
  // "Stop sounds".
  const phoneShort = mobile;
  // Cycle 81: gate on the phase-live predicate + the persisted
  // episode level — NOT on the live reading's instantaneous zone.
  // During the recovery-dwell window the reading has risen back into
  // range (`episode.level` / `currentLevel` is null) while the
  // episode is still live; gating on `episode.level === "low"` here
  // was the bug that left the bar mounted-but-empty with the loop
  // still able to sound. `episodeLevel` falls back to the episode's
  // start level through the dwell so the banner stays populated.
  if (!episode.isLive || episode.episodeLevel !== "low") return null;
  // Cycle 304, WIDENED IN CYCLE 398: a low that is back in range on a
  // fresh, strictly-newer reading hides immediately — in ANY phase now, not
  // only when muted. The server still owns recovery; the banner just stops
  // waiting out the 90 s dwell (a SECOND reading on a 5-minute cadence).
  // Reappears the instant the reading goes alarm-side again, in the same
  // render, with no new alert row.
  //
  // The cycle-81 floor still holds: `audioOn` is DERIVED from this
  // predicate, so when this returns null the loop is already silent —
  // "a control whenever audio could sound" is satisfied by audio not being
  // able to sound. See `lib/alarmStandDown.ts`.
  if (episode.bannerSuppressedInRange) return null;
  // Cycle 81: exhaustive, mutually-exclusive states with a default
  // branch that ALWAYS renders a control — an empty fall-through is
  // the bug being fixed and must be structurally impossible. The old
  // `showActive` gated on `!hasSnoozed`, so once a snooze elapsed and
  // `phase` returned to "active" (re-armed) with `hasSnoozed` still
  // true, none of the three branches matched and the bar went empty
  // while audio looped. Now the active branch is the default: any
  // live, non-snoozed, non-stopped state shows Snooze + Stop.
  // ⚠ CYCLE 416: A FAILED ACK OUTRANKS THE SNOOZED / STOPPED PRESENTATION.
  // If the ack never landed, the alarm is still live — so the banner must
  // NOT render the calm state the user was expecting. Forcing `showActive`
  // does three things at once: it stops the UI claiming a snooze the server
  // never granted, it puts the controls back on screen (the standing rule
  // that at least one alarm control is present whenever audio can sound),
  // and those controls ARE the retry affordance — pressing Snooze again
  // re-runs the ack.
  const ackFailed = episode.ackFailure != null;
  const showSnoozing = !ackFailed && episode.phase === "snoozed";
  const showStopped = !ackFailed && !showSnoozing && episode.stopped;
  const showActive = !showSnoozing && !showStopped;
  const sgvText =
    episode.sgv != null ? displayValue(episode.sgv, units) : "—";

  // Cycle 166's clock-mode takeover (a sounding Low covering the whole
  // bedside face) is REMOVED in cycle 454 by operator decision: Urgent Low is
  // the only full-screen takeover. In clock mode every state is the fixed
  // bottom strip below, with the same Snooze + Stop and the same sound.

  return (
    <div
      role="status"
      aria-live="polite"
      // Cycle 85 / 103: the `.kiosk`-scoped rule keeps this strip above
      // any kiosk chrome (it originally lifted it over the cycle-79 curved
      // rail seam, removed in cycle 103; the rule stays as a defensive
      // floor). No-op on web/mobile (no `.kiosk` ancestor). See tokens.css
      // `.alarmBannerStrip`.
      className="alarmBannerStrip"
      // Cycle 452.2: an inert marker the type-floor sweep finds alarm
      // controls by (44 px buttons, 4.5:1 text).
      data-alarm-control="banner"
      style={{
        flexShrink: 0,
        // Cycle 452.1 (phones): the row may GROW when the status suffix has
        // to wrap ("· NOT SNOOZED" is alarm state and is never cut off).
        // Kiosk keeps the fixed single row; web may wrap (cycle 460, below).
        // Cycle 452.2: the row carries 44 px buttons now (it held 32 px ones
        // in a 40 px row) — a single line is 52 px on every surface.
        ...(mobile
          ? { minHeight: ALARM_BANNER_ROW_PX, padding: `${ALARM_BANNER_PAD_Y}px 16px` }
          : { height: ALARM_BANNER_ROW_PX, padding: "0 16px" }),
        // Cycle 460 (measured): the web strip lives in the `.alarmRow`
        // column (≤ 600 px). With a failed ack, "LOW · NOT SNOOZED" + the
        // reading + the two pills need ≈ 615 px; alarm-state text is never
        // cut (452.1) and the pills never shrink (WS1), so on WEB ONLY the
        // row may wrap and the pair drops to a right-aligned second line.
        // Whenever it fits — every other state — it is the same 52 px row.
        // The kiosk strip is a full-width bar and keeps its fixed row.
        ...(variant === "web"
          ? {
              height: undefined,
              minHeight: ALARM_BANNER_ROW_PX,
              padding: `${ALARM_BANNER_PAD_Y}px 16px`,
              flexWrap: "wrap" as const,
              rowGap: 4,
            }
          : null),
        // Cycle 452.2: an OPAQUE amber with deep-shade ink (about 9:1). The
        // cycle-91.1 translucent kiosk fill and its white copy read at under
        // 2:1 on amber, and at no bound at all over a photo.
        ...alarmBannerGround("warn"),
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontFamily: "inherit",
        fontWeight: 700,
        // Cycle 302: smaller banner text + a single-line guarantee so the
        // longer urgent labels ("URGENT LOW · 55 mg/dL") never wrap to a
        // second line inside this fixed ~40 px row. `white-space` is
        // inherited, so setting it on the flex container pins every text
        // child (label, sgv, subtext) to one line; the flex items no
        // longer shrink-and-wrap.
        fontSize: 13,
        letterSpacing: "0.02em",
        whiteSpace: "nowrap",
        boxShadow: "0 1px 0 rgba(0, 0, 0, 0.18)",
        // Cycle 452.2: the Stopped strip is no longer dimmed with `opacity` —
        // that lowered its text's contrast. The still dot and "Low · Stopped"
        // carry the muted state.
        // Cycle 167: while the clock face is active, the collapsed strip
        // pins FULL-WIDTH to the bottom edge of the face (the face
        // reserves the band via `reserveBottomStrip`, so the heroes
        // never sit under it). Same content/state machine; the shadow
        // flips upward off the bottom edge. Stays inside the clock-mode
        // banner row's z-50 context — below AlarmSilenceGuard (60) and
        // UrgentOverlay (100). Outside clock mode: byte-identical.
        ...(clockModeActive
          ? {
              position: "fixed" as const,
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 50,
              boxShadow: "0 -1px 0 rgba(0, 0, 0, 0.18)",
            }
          : null),
      }}
    >
      <span
        aria-hidden
        // Cycle 62: dot stops pulsing in the muted (stopped) state so a
        // glance can tell whether the alarm is currently sounding or
        // muted-but-watching. Cycle 452.2: drawn in the banner's ink.
        style={alarmBannerDot(!showStopped)}
      />
      <span
        style={{
          textTransform: "uppercase",
          letterSpacing: mobile ? "0.06em" : "0.08em",
          // Cycle 303 (mobile): the label + merged status is the single
          // flexible element; it ellipsizes so it can never push the
          // action cluster off-screen or force a wrap.
          // Cycle 452.1 (phones): the label WRAPS instead of ellipsizing —
          // each status fragment stays whole (its own nowrap span), so a
          // break can only fall between "Low" and "· NOT SNOOZED". The
          // action cluster still never shrinks.
          ...(mobile
            ? {
                flex: "1 1 auto",
                minWidth: 0,
                whiteSpace: "normal",
                lineHeight: 1.25,
              }
            : null),
        }}
      >
        <span style={{ whiteSpace: "nowrap" }}>
          {showStopped ? "Low · Stopped" : "Low"}
        </span>
        {/* Cycle 416: name the failure inline. The banner is a
            fixed single-line row, so this is a suffix rather than a
            second row — the full sentence lives on the roomier
            surfaces. It never claims the alarm is handled. */}
        {ackFailed ? (
          <>
            {" "}
            <span style={{ whiteSpace: "nowrap" }} data-testid="ack-suffix">
              {episode.ackFailure?.action === "snooze" ? "· NOT SNOOZED" : "· NOT STOPPED"}
            </span>
          </>
        ) : null}
        {/* Cycle 303 (mobile): merge the snooze countdown into the text
            block as "Low · Snoozing M:SS" (kiosk/web keep it as the
            separate right-anchored span below). */}
        {mobile && showSnoozing ? (
          <>
            {" "}
            <span style={{ whiteSpace: "nowrap" }}>
              {`· Snoozing ${fmtSnooze(episode.snoozeRemainingMs ?? 0)}`}
            </span>
          </>
        ) : null}
      </span>
      {/* Cycle 303: the glucose reading is dropped on the mobile strip
          (it's on the Now hero). Kiosk/web keep it byte-identical. */}
      {!mobile && (
        <>
          <span style={{ opacity: 0.7 }} aria-hidden>
            ·
          </span>
          <span className="tnum">
            {sgvText} {units}
          </span>
          <span style={{ flex: 1 }} />
        </>
      )}
      {showActive && (
        // Cycle 460 (operator decision): Snooze and Stop are the shared alarm
        // pills — the high banner's Stop-pill geometry — right-aligned as a
        // pair at one shared width: Snooze `filled`, Stop `outlined`. Same
        // order and the same handlers as before; only the drawing and the
        // words changed ("… sounds": they quiet the audio, not the low). On
        // a portrait phone the pair stays on the label's row (cycle 461).
        <div
          style={pillHolderStyle}
        >
          <AlarmPillPair>
            <AlarmPill
              tone="warn"
              label={LOW_SNOOZE_LABEL}
              shortLabel={phoneShort ? LOW_SNOOZE_SHORT_LABEL : undefined}
              onPress={() => void episode.address()}
              marker={FILLED_MARKER}
            />
            <AlarmPill
              tone="warn"
              label={LOW_STOP_LABEL}
              shortLabel={phoneShort ? LOW_STOP_SHORT_LABEL : undefined}
              appearance="outlined"
              onPress={episode.stop}
              marker={OUTLINED_MARKER}
            />
          </AlarmPillPair>
        </div>
      )}
      {showSnoozing && !mobile && (
        <span
          className="tnum"
          style={{
            fontSize: 13,
            fontWeight: 700,
            whiteSpace: "nowrap",
          }}
        >
          Snoozing {fmtSnooze(episode.snoozeRemainingMs ?? 0)}
        </span>
      )}
      {showStopped && (
        // Cycle 460: the one remaining control is a `filled` pill at the
        // shared size, reading "Snooze sounds" (its `aria-label` is the
        // visible text — the cycle-62 long label is retired).
        <div
          style={pillHolderStyle}
        >
          <AlarmPillPair>
            <AlarmPill
              tone="warn"
              label={LOW_SNOOZE_LABEL}
              shortLabel={phoneShort ? LOW_SNOOZE_SHORT_LABEL : undefined}
              onPress={() => void episode.address()}
              marker={FILLED_MARKER}
            />
          </AlarmPillPair>
        </div>
      )}
    </div>
  );
}

// Cycle 460: the holder the pill pair sits in. Cycle 303 (WS1): the action
// cluster never shrinks — the label is the only flexible element. It is
// right-aligned by its pair (kiosk / web: after the row's flex spacer).
const pillHolderStyle: React.CSSProperties = {
  display: "inline-flex",
  justifyContent: "flex-end",
  alignItems: "center",
  flexShrink: 0,
  marginLeft: "auto",
};

// Inert markers the sweeps and tests find the Low pills by.
const FILLED_MARKER = { "data-alarm-pill": "filled" } as const;
const OUTLINED_MARKER = { "data-alarm-pill": "outlined" } as const;
